import { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  getEconomySettings,
  getAdminPackages,
  saveEconomySettings,
  updateAdminPackage,
  createAdminPackage,
  deleteAdminPackage,
  uploadPackageImage,
  type EconomySettings,
  type AdminPackageItem,
  getProfileColors,
  createProfileColor,
  updateProfileColor,
  getProfileFrames,
  createProfileFrame,
  updateProfileFrame,
  uploadProfileFrameImage,
  type ProfileColorOptionDto,
  type ProfileFrameOptionDto,
  getProfileBackgrounds,
  createProfileBackground,
  updateProfileBackground,
  uploadProfileBackgroundImage,
  type ProfileBackgroundOptionDto,
} from "../api/adminEconomyApi";
import Swal from "sweetalert2";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import { getAdminLogs, type AdminLogItem } from "../api/adminLogsApi";
import { useToast } from "../context/ToastContext";

type EditablePackageItem = AdminPackageItem & {
  colorOptionIds: string[];
  frameOptionIds: string[];
  backgroundOptionIds: string[];
};

interface NewPackageDraft {
  name: string;
  dailyQuotaPremium: number;
  dailyQuotaFree: number;
  durationDays: number;
  priceZ: number;
  description: string;
  imageUrl?: string | null;
  colorOptionIds: string[];
  frameOptionIds: string[];
  backgroundOptionIds: string[];
}

const UPLOAD_BASE = (process.env.REACT_APP_UPLOAD_BASE_URL ?? "").replace(/\/$/, "");

const resolveAssetUrl = (raw?: string | null): string | undefined => {
  if (!raw) {
    return undefined;
  }

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const trimmed = raw.replace(/^\/+/, "");
  const relative = trimmed.startsWith("uploads/") ? trimmed : `uploads/${trimmed}`;

  if (UPLOAD_BASE) {
    return `${UPLOAD_BASE}/${relative}`;
  }

  return `/${relative}`;
};

const normalizePackages = (items: AdminPackageItem[]): EditablePackageItem[] =>
  items.map((pkg) => ({
    ...pkg,
    colorOptionIds: Array.isArray(pkg.colorOptionIds) ? [...pkg.colorOptionIds] : [],
    frameOptionIds: Array.isArray(pkg.frameOptionIds) ? [...pkg.frameOptionIds] : [],
    backgroundOptionIds: Array.isArray(pkg.backgroundOptionIds) ? [...pkg.backgroundOptionIds] : [],
  }));

const BACKGROUND_PREVIEW_FALLBACK = "linear-gradient(135deg, #4c1d95, #0ea5e9, #10b981)";

const getBackgroundPreviewStyle = (background: ProfileBackgroundOptionDto): string => {
  if (background.cssValue) {
    return background.cssValue;
  }
  const asset = resolveAssetUrl(background.previewUrl || background.imageUrl);return asset ? `url(${asset})` : "";
};

export default function AdminEconomy() {
  const { loading: checkingPermission, allowed } = usePermissionCheck("Economy.View");
  const { allowed: canEditSettings } = usePermissionCheck("Economy.Settings");
  const { allowed: canEditPackages } = usePermissionCheck("Economy.Packages");
  const toast = useToast();
  const [settingsEdit, setSettingsEdit] = useState<EconomySettings>({
    freeDailyQuota: 10,
    singleUnlockPriceZ: 5,
    creatorDailyPartLimit: 3,
    creatorRevenueSharePercent: 65,
    tokenToTryRate: 1,
    creatorPayoutCurrencyCode: "TRY",
  });
  const [packages, setPackages] = useState<EditablePackageItem[]>([]);
  const [colors, setColors] = useState<ProfileColorOptionDto[]>([]);
  const [frames, setFrames] = useState<ProfileFrameOptionDto[]>([]);
  const [backgrounds, setBackgrounds] = useState<ProfileBackgroundOptionDto[]>([]);
  const [packageFilter, setPackageFilter] = useState("");
  const [auditLogItems, setAuditLogItems] = useState<AdminLogItem[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [newColor, setNewColor] = useState<{ name: string; hexCode: string; sortOrder: number; isActive: boolean }>({
    name: "",
    hexCode: "#FFFFFF",
    sortOrder: 0,
    isActive: true,
  });
  const [newFrame, setNewFrame] = useState<{ name: string; imageUrl: string; sortOrder: number; isActive: boolean }>({
    name: "",
    imageUrl: "",
    sortOrder: 0,
    isActive: true,
  });
  const [newBackground, setNewBackground] = useState<{
    name: string;
    cssValue: string;
    imageUrl: string;
    previewUrl: string;
    sortOrder: number;
    isActive: boolean;
    isDefault: boolean;
  }>({
    name: "",
    cssValue: "linear-gradient(135deg, #4c1d95, #0ea5e9, #10b981)",
    imageUrl: "",
    previewUrl: "",
    sortOrder: 0,
    isActive: true,
    isDefault: false,
  });
  const [uploadingCreate, setUploadingCreate] = useState(false);
  const [uploadingPackageId, setUploadingPackageId] = useState<string | null>(null);
  const [uploadingFrameCreate, setUploadingFrameCreate] = useState(false);
  const [uploadingFrameOptionId, setUploadingFrameOptionId] = useState<string | null>(null);
  const [uploadingBackgroundCreate, setUploadingBackgroundCreate] = useState(false);
  const [uploadingBackgroundOptionId, setUploadingBackgroundOptionId] = useState<string | null>(null);
  const [newPkg, setNewPkg] = useState<NewPackageDraft>({
    name: "",
    dailyQuotaPremium: 0,
    dailyQuotaFree: 0,
    durationDays: 30,
    priceZ: 0,
    description: "",
    imageUrl: undefined,
    colorOptionIds: [],
    frameOptionIds: [],
    backgroundOptionIds: [],
  });
  const loadAuditTrail = useCallback(async () => {
    if (!allowed) {
      return;
    }
    setAuditLoading(true);
    const response = await getAdminLogs({ area: "economy", action: "settings_update", page: 1, pageSize: 5 });
    setAuditLogItems(response.items);
    setAuditLoading(false);
  }, [allowed]);

  useEffect(() => {
    const load = async () => {
      const [s, p, colorList, frameList, backgroundList] = await Promise.all([
        getEconomySettings(),
        getAdminPackages(),
        getProfileColors(),
        getProfileFrames(),
        getProfileBackgrounds(),
      ]);
      setSettingsEdit({
        freeDailyQuota: s.freeDailyQuota,
        singleUnlockPriceZ: s.singleUnlockPriceZ,
        creatorDailyPartLimit: s.creatorDailyPartLimit,
        creatorRevenueSharePercent: s.creatorRevenueSharePercent,
        tokenToTryRate: s.tokenToTryRate,
        creatorPayoutCurrencyCode: s.creatorPayoutCurrencyCode,
      });
      setPackages(normalizePackages(p));
      setColors(colorList);
      setFrames(frameList);
      setBackgrounds(backgroundList);
      await loadAuditTrail();
    };
    if (allowed) {
      load();
    }
  }, [allowed, loadAuditTrail]);

  if (checkingPermission) {
    return <div className="p-8 text-center text-sm text-gray-500">Checking permissions...</div>;
  }

  if (!allowed) {
    return <div className="p-8 text-center text-sm text-red-500">Bu sayfaya erişim yetkin yok.</div>;
  }

  const refreshPackages = async () => {
    const list = await getAdminPackages();
    setPackages(normalizePackages(list));
  };

  const refreshColors = async () => {
    const list = await getProfileColors();
    setColors(list);
  };

  const refreshBackgrounds = async () => {
    const list = await getProfileBackgrounds();
    setBackgrounds(list);
  };

  const refreshFrames = async () => {
    const list = await getProfileFrames();
    setFrames(list);
  };

  const filteredPackages = packageFilter.trim()
    ? packages.filter((pkg) => pkg.name.toLowerCase().includes(packageFilter.trim().toLowerCase()))
    : packages;

  const saveSettingsClick = async () => {
    if (!canEditSettings) {
      Swal.fire("Yetki yok", "Ekonomi ayarlarını güncelleme yetkin yok.", "warning");
      return;
    }
    const res = await saveEconomySettings(settingsEdit);
    if (res.success) {
      toast.success("Economy settings updated.");
      void loadAuditTrail();
    } else {
      toast.error("Could not save economy settings.");
    }
  };

  const savePackage = async (pkg: EditablePackageItem, opts?: { successMessage?: string; silent?: boolean }) => {
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Paketleri güncelleme yetkin bulunmuyor.", "warning");
      return;
    }
    const res = await updateAdminPackage({
      id: pkg.id,
      name: pkg.name,
      dailyQuotaPremium: pkg.dailyQuotaPremium,
      dailyQuotaFree: pkg.dailyQuotaFree,
      durationDays: pkg.durationDays,
      priceZ: pkg.priceZ,
      description: pkg.description ?? null,
      imageUrl: pkg.imageUrl ?? null,
      colorOptionIds: pkg.colorOptionIds,
      frameOptionIds: pkg.frameOptionIds,
      backgroundOptionIds: pkg.backgroundOptionIds,
    });
    if (res.success) {
      const message = opts?.successMessage ?? (opts?.silent ? null : "Package updated.");
      if (message) {
        Swal.fire("Saved", message, "success");
      }
    } else {
      Swal.fire("Error", "Package update failed.", "error");
    }
  };

  const createPackageClick = async () => {
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Yeni paket oluşturma yetkin bulunmuyor.", "warning");
      return;
    }
    if (!newPkg.name.trim()) {
      Swal.fire("Warning", "Please provide a package name.", "warning");
      return;
    }

    const res = await createAdminPackage({
      name: newPkg.name.trim(),
      dailyQuotaPremium: newPkg.dailyQuotaPremium,
      dailyQuotaFree: newPkg.dailyQuotaFree,
      durationDays: newPkg.durationDays,
      priceZ: newPkg.priceZ,
      description: newPkg.description.trim() || null,
      imageUrl: newPkg.imageUrl ?? null,
      colorOptionIds: newPkg.colorOptionIds,
      frameOptionIds: newPkg.frameOptionIds,
      backgroundOptionIds: newPkg.backgroundOptionIds,
    });

    if (res.success) {
      Swal.fire("Created", "New package created successfully.", "success");
      await refreshPackages();
      setNewPkg({
        name: "",
        dailyQuotaPremium: 0,
        dailyQuotaFree: 0,
        durationDays: 30,
        priceZ: 0,
        description: "",
        imageUrl: undefined,
        colorOptionIds: [],
        frameOptionIds: [],
        backgroundOptionIds: [],
      });
    } else {
      Swal.fire("Error", "Failed to create package.", "error");
    }
  };

  const deletePackage = async (pkg: EditablePackageItem) => {
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Paket silemezsin.", "warning");
      return;
    }
    const confirmation = await Swal.fire({
      title: "Delete this package?",
      text: `${pkg.name} will be removed permanently.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    const res = await deleteAdminPackage(pkg.id);
    if (res.success) {
      setPackages((arr) => arr.filter((p) => p.id !== pkg.id));
      Swal.fire("Deleted", "Package removed.", "success");
    } else {
      Swal.fire("Error", "Failed to delete package.", "error");
    }
  };

  const handleNewPackageImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) {
      return;
    }

    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Paket görseli yükleme yetkin bulunmuyor.", "warning");
      return;
    }

    setUploadingCreate(true);
    try {
      const fileName = await uploadPackageImage(file);
      if (!fileName) {
        throw new Error("empty file name");
      }

      setNewPkg((prev) => ({ ...prev, imageUrl: fileName }));
      Swal.fire("Uploaded", "Görsel yüklendi. Paketi kaydetmeyi unutma.", "success");
    } catch (error) {
      console.error("[admin] upload package image failed", error);
      Swal.fire("Error", "Görsel yüklenemedi.", "error");
    } finally {
      setUploadingCreate(false);
    }
  };

  const clearNewPackageImage = () => {
    setNewPkg((prev) => ({ ...prev, imageUrl: undefined }));
  };

  const handlePackageImageChange = async (pkg: EditablePackageItem, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) {
      return;
    }

    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Paket görsellerini güncelleme yetkin bulunmuyor.", "warning");
      return;
    }

    setUploadingPackageId(pkg.id);
    try {
      const fileName = await uploadPackageImage(file);
      if (!fileName) {
        throw new Error("empty file name");
      }

      const updated: EditablePackageItem = { ...pkg, imageUrl: fileName };
      setPackages((arr) => arr.map((p) => (p.id === pkg.id ? updated : p)));
      await savePackage(updated, { successMessage: "Paket görseli güncellendi." });
    } catch (error) {
      console.error("[admin] upload package image failed", error);
      Swal.fire("Error", "Görsel yüklenemedi.", "error");
    } finally {
      setUploadingPackageId(null);
    }
  };

  const clearPackageImage = async (pkg: EditablePackageItem) => {
    if (!pkg.imageUrl) {
      return;
    }

    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Paket görsellerini kaldırma yetkin bulunmuyor.", "warning");
      return;
    }

    const confirmation = await Swal.fire({
      title: "Görsel kaldırılsın mı?",
      text: "Bu paketin görseli silinecek.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f97316",
      confirmButtonText: "Kaldır",
      cancelButtonText: "Vazgeç",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    const updated: EditablePackageItem = { ...pkg, imageUrl: undefined };
    setPackages((arr) => arr.map((p) => (p.id === pkg.id ? updated : p)));
    await savePackage(updated, { successMessage: "Paket görseli kaldırıldı." });
  };

  const toggleNewPackageColor = (colorId: string) => {
    setNewPkg((prev) => {
      const exists = prev.colorOptionIds.includes(colorId);
      return {
        ...prev,
        colorOptionIds:exists ? prev.colorOptionIds.filter((id) => id !== colorId) : [...prev.colorOptionIds, colorId],
      };
    });
  };

  const toggleNewPackageFrame = (frameId: string) => {
    setNewPkg((prev) => {
      const exists = prev.frameOptionIds.includes(frameId);
      return {
        ...prev,
        frameOptionIds:exists ? prev.frameOptionIds.filter((id) => id !== frameId) : [...prev.frameOptionIds, frameId],
      };
    });
  };

  const toggleNewPackageBackground = (backgroundId: string) => {
    setNewPkg((prev) => {
      const exists = prev.backgroundOptionIds.includes(backgroundId);
      return {
        ...prev,
        backgroundOptionIds: exists
          ? prev.backgroundOptionIds.filter((id) => id !== backgroundId)
          : [...prev.backgroundOptionIds, backgroundId],
      };
    });
  };

  const togglePackageColor = (packageId: string, colorId: string) => {
    setPackages((arr) =>
      arr.map((pkg) =>
        pkg.id === packageId
          ? {
              ...pkg,
              colorOptionIds: pkg.colorOptionIds.includes(colorId)
                ? pkg.colorOptionIds.filter((id) => id !== colorId)
                : [...pkg.colorOptionIds, colorId],
            }
          : pkg,
      ),
    );
  };

  const togglePackageFrame = (packageId: string, frameId: string) => {
    setPackages((arr) =>
      arr.map((pkg) =>
        pkg.id === packageId
          ? {
              ...pkg,
              frameOptionIds: pkg.frameOptionIds.includes(frameId)
                ? pkg.frameOptionIds.filter((id) => id !== frameId)
                : [...pkg.frameOptionIds, frameId],
            }
          : pkg,
      ),
    );
  };

  const togglePackageBackground = (packageId: string, backgroundId: string) => {
    setPackages((arr) =>
      arr.map((pkg) =>
        pkg.id === packageId
          ? {
              ...pkg,
              backgroundOptionIds: pkg.backgroundOptionIds.includes(backgroundId)
                ? pkg.backgroundOptionIds.filter((id) => id !== backgroundId)
                : [...pkg.backgroundOptionIds, backgroundId],
            }
          : pkg,
      ),
    );
  };

  const handleCreateColor = async () => {
    if (!newColor.name.trim()) {
      Swal.fire("Uyarı", "Renk adı zorunludur.", "warning");
      return;
    }
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Renk ekleme yetkin bulunmuyor.", "warning");
      return;
    }
    const normalizedHex = newColor.hexCode.startsWith("#") ? newColor.hexCode : `#${newColor.hexCode}`;
    const success = await createProfileColor({
      name: newColor.name.trim(),
      hexCode: normalizedHex,
      sortOrder: Number(newColor.sortOrder) || 0,
      isActive: newColor.isActive,
    });
    if (success) {
      Swal.fire("Kaydedildi", "Yeni renk eklendi.", "success");
      setNewColor({ name: "", hexCode: "#FFFFFF", sortOrder: 0, isActive: true });
      await refreshColors();
    } else {
      Swal.fire("Hata", "Renk kaydedilemedi.", "error");
    }
  };

  const handleUpdateColor = async (color: ProfileColorOptionDto) => {
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Renk güncelleme yetkin yok.", "warning");
      return;
    }
    const normalizedHex = color.hexCode.startsWith("#") ? color.hexCode : `#${color.hexCode}`;
    const success = await updateProfileColor(color.id, {
      name: color.name.trim(),
      hexCode: normalizedHex,
      sortOrder: Number(color.sortOrder) || 0,
      isActive: color.isActive,
    });
    if (success) {
      Swal.fire("Kaydedildi", "Renk güncellendi.", "success");
      await refreshColors();
    } else {
      Swal.fire("Hata", "Renk güncellenemedi.", "error");
    }
  };

  const handleCreateFrame = async () => {
    if (!newFrame.name.trim() || !newFrame.imageUrl) {
      Swal.fire("Uyarı", "Çerçeve adı ve görseli zorunludur.", "warning");
      return;
    }
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Çerçeve ekleyemezsin.", "warning");
      return;
    }
    const success = await createProfileFrame({
      name: newFrame.name.trim(),
      imageUrl: newFrame.imageUrl,
      sortOrder: Number(newFrame.sortOrder) || 0,
      isActive: newFrame.isActive,
    });
    if (success) {
      Swal.fire("Kaydedildi", "Yeni çerçeve eklendi.", "success");
      setNewFrame({ name: "", imageUrl: "", sortOrder: 0, isActive: true });
      await refreshFrames();
    } else {
      Swal.fire("Hata", "Çerçeve eklenemedi.", "error");
    }
  };

  const handleUpdateFrame = async (frame: ProfileFrameOptionDto, { imageOverride }: { imageOverride?: string } = {}) => {
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Çerçeve güncelleme yetkin yok.", "warning");
      return;
    }
    const success = await updateProfileFrame(frame.id, {
      name: frame.name.trim(),
      imageUrl: imageOverride ?? frame.imageUrl,
      sortOrder: Number(frame.sortOrder) || 0,
      isActive: frame.isActive,
    });
    if (success) {
      Swal.fire("Kaydedildi", "Çerçeve güncellendi.", "success");
      await refreshFrames();
    } else {
      Swal.fire("Hata", "Çerçeve güncellenemedi.", "error");
    }
  };

  const handleNewFrameImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Çerçeve görseli yükleme yetkin yok.", "warning");
      return;
    }
    setUploadingFrameCreate(true);
    try {
      const fileName = await uploadProfileFrameImage(file);
      if (!fileName) {
        throw new Error("empty file name");
      }
      setNewFrame((prev) => ({ ...prev, imageUrl: fileName }));
      Swal.fire("Yüklendi", "Çerçeve görseli yüklendi. Kaydetmeyi unutma.", "success");
    } catch (error) {
      console.error("[admin] upload frame image failed", error);
      Swal.fire("Hata", "Çerçeve görseli yüklenemedi.", "error");
    } finally {
      setUploadingFrameCreate(false);
    }
  };

  const handleExistingFrameImageChange = async (frame: ProfileFrameOptionDto, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Çerçeve görsellerini güncelleme yetkin yok.", "warning");
      return;
    }
    setUploadingFrameOptionId(frame.id);
    try {
      const fileName = await uploadProfileFrameImage(file);
      if (!fileName) {
        throw new Error("empty file name");
      }
      await handleUpdateFrame(frame, { imageOverride: fileName });
    } catch (error) {
      console.error("[admin] upload frame image failed", error);
      Swal.fire("Hata", "Çerçeve görseli güncellenemedi.", "error");
    } finally {
      setUploadingFrameOptionId(null);
    }
  };

  const handleCreateBackground = async () => {
    if (!newBackground.name.trim()) {
      Swal.fire("Uyarı", "Arkaplan adı zorunludur.", "warning");
      return;
    }
    if (!newBackground.cssValue.trim() && !newBackground.imageUrl.trim()) {
      Swal.fire("Uyarı", "CSS veya görsel yolundan en az biri gerekli.", "warning");
      return;
    }
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Arkaplan ekleme yetkin bulunmuyor.", "warning");
      return;
    }

    const success = await createProfileBackground({
      name: newBackground.name.trim(),
      cssValue: newBackground.cssValue.trim() || undefined,
      imageUrl: newBackground.imageUrl.trim() || undefined,
      previewUrl: newBackground.previewUrl.trim() || undefined,
      sortOrder: Number(newBackground.sortOrder) || 0,
      isActive: newBackground.isActive,
      isDefault: newBackground.isDefault,
    });

    if (success) {
      Swal.fire("Kaydedildi", "Yeni arkaplan eklendi.", "success");
      setNewBackground({
        name: "",
        cssValue: "linear-gradient(135deg, #4c1d95, #0ea5e9, #10b981)",
        imageUrl: "",
        previewUrl: "",
        sortOrder: 0,
        isActive: true,
        isDefault: false,
      });
      await refreshBackgrounds();
    } else {
      Swal.fire("Hata", "Arkaplan kaydedilemedi.", "error");
    }
  };

  const handleUpdateBackground = async (background: ProfileBackgroundOptionDto) => {
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Arkaplan güncelleme yetkin yok.", "warning");
      return;
    }
    if (!background.name.trim()) {
      Swal.fire("Uyarı", "Arkaplan adı zorunludur.", "warning");
      return;
    }
    const success = await updateProfileBackground(background.id, {
      name: background.name.trim(),
      cssValue: background.cssValue?.trim() || undefined,
      imageUrl: background.imageUrl?.trim() || undefined,
      previewUrl: background.previewUrl?.trim() || undefined,
      sortOrder: Number(background.sortOrder) || 0,
      isActive: background.isActive,
      isDefault: Boolean(background.isDefault),
    });
    if (success) {
      Swal.fire("Kaydedildi", "Arkaplan güncellendi.", "success");
      await refreshBackgrounds();
    } else {
      Swal.fire("Hata", "Arkaplan güncellenemedi.", "error");
    }
  };

  const handleBackgroundImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Arkaplan görseli yükleme yetkin yok.", "warning");
      return;
    }
    setUploadingBackgroundCreate(true);
    try {
      const fileName = await uploadProfileBackgroundImage(file);
      if (!fileName) {
        throw new Error("empty file name");
      }
      setNewBackground((prev) => ({ ...prev, imageUrl: fileName, previewUrl: fileName }));
      Swal.fire("Yüklendi", "Arkaplan görseli yüklendi. Kaydetmeyi unutma.", "success");
    } catch (error) {
      console.error("[admin] upload background image failed", error);
      Swal.fire("Hata", "Arkaplan görseli yüklenemedi.", "error");
    } finally {
      setUploadingBackgroundCreate(false);
    }
  };

  const handleExistingBackgroundImageChange = async (
    background: ProfileBackgroundOptionDto,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!canEditPackages) {
      Swal.fire("Yetki yok", "Arkaplan görsellerini güncelleme yetkin yok.", "warning");
      return;
    }
    setUploadingBackgroundOptionId(background.id);
    try {
      const fileName = await uploadProfileBackgroundImage(file);
      if (!fileName) {
        throw new Error("empty file name");
      }
      await handleUpdateBackground({ ...background, imageUrl: fileName, previewUrl: fileName });
    } catch (error) {
      console.error("[admin] upload background image failed", error);
      Swal.fire("Hata", "Arkaplan görseli güncellenemedi.", "error");
    } finally {
      setUploadingBackgroundOptionId(null);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Economy &amp; Packages</h1>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        <StatBox label="Paket sayısı" value={packages.length} />
        <StatBox label="Renk seçenekleri" value={colors.length} />
        <StatBox label="Çerçeve seçenekleri" value={frames.length} />
        <StatBox label="Arka plan seçenekleri" value={backgrounds.length} />
        <StatBox label="Günlük ücretsiz kota" value={settingsEdit.freeDailyQuota} />
        <StatBox label="Tek bölüm ücreti (Z)" value={settingsEdit.singleUnlockPriceZ} />
        <StatBox label="Creator pay (%)" value={`${settingsEdit.creatorRevenueSharePercent}%`} />
        <StatBox
          label={`1 ZTN ≈ ${settingsEdit.creatorPayoutCurrencyCode}`}
          value={`${settingsEdit.tokenToTryRate} ${settingsEdit.creatorPayoutCurrencyCode}`}
        />
      </div>

      <div className="bg-white rounded shadow p-4 text-sm">
        <div className="font-medium mb-2">Global Economy Settings</div>
        <div className="grid gap-4 sm:grid-cols-6 items-end">
          <label className="text-sm">
            <div className="text-gray-600">Free daily premium quota</div>
            <input
              type="number"
              className="mt-1 w-full border rounded p-2"
              value={settingsEdit.freeDailyQuota}
              onChange={(e) => setSettingsEdit({ ...settingsEdit, freeDailyQuota: Number(e.target.value) })}
            />
          </label>
          <label className="text-sm">
            <div className="text-gray-600">Single unlock price (Z)</div>
            <input
              type="number"
              className="mt-1 w-full border rounded p-2"
              value={settingsEdit.singleUnlockPriceZ}
              onChange={(e) => setSettingsEdit({ ...settingsEdit, singleUnlockPriceZ: Number(e.target.value) })}
            />
          </label>
          <label className="text-sm">
            <div className="text-gray-600">Creator daily chapter limit</div>
            <input
              type="number"
              className="mt-1 w-full border rounded p-2"
              value={settingsEdit.creatorDailyPartLimit}
              onChange={(e) => setSettingsEdit({ ...settingsEdit, creatorDailyPartLimit: Number(e.target.value) })}
              min={1}
            />
          </label>
          <label className="text-sm">
            <div className="text-gray-600">Creator revenue share (%)</div>
            <input
              type="number"
              className="mt-1 w-full border rounded p-2"
              value={settingsEdit.creatorRevenueSharePercent}
              onChange={(e) => setSettingsEdit({ ...settingsEdit, creatorRevenueSharePercent: Number(e.target.value) })}
              min={0}
              max={100}
            />
          </label>
          <label className="text-sm">
            <div className="text-gray-600">1 ZTN ≈ (currency)</div>
            <div className="mt-1 flex gap-2">
              <input
                type="number"
                className="w-full border rounded p-2"
                value={settingsEdit.tokenToTryRate}
                onChange={(e) => setSettingsEdit({ ...settingsEdit, tokenToTryRate: Number(e.target.value) })}
                min={0}
                step={0.01}
              />
              <input
                type="text"
                className="w-20 border rounded p-2 uppercase"
                value={settingsEdit.creatorPayoutCurrencyCode}
                onChange={(e) => setSettingsEdit({ ...settingsEdit, creatorPayoutCurrencyCode: e.target.value.toUpperCase() })}
                maxLength={8}
              />
            </div>
          </label>
          <button
            onClick={saveSettingsClick}
            className="h-9 rounded bg-blue-600 px-4 text-sm text-white hover:bg-blue-700 sm:col-span-1"
          >
            Save settings
          </button>
        </div>
      </div>

      <div className="rounded bg-white p-4 shadow text-sm">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-base font-semibold text-slate-800">Son Ekonomi Değişiklikleri</div>
            <p className="text-xs text-slate-500">Bu tabloda kimlerin ekonomi ayarlarını güncellediğini görebilirsin.</p>
          </div>
          <button
            type="button"
            onClick={() => void loadAuditTrail()}
            className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
          >
            Yenile
          </button>
        </div>
        <div className="mt-3 divide-y rounded border border-slate-200">
          {auditLoading ? (
            <div className="p-3 text-xs text-slate-500">Kayıtlar yükleniyor...</div>
          ) : auditLogItems.length === 0 ? (
            <div className="p-3 text-xs text-slate-500">Henüz kayıt bulunamadı.</div>
          ) : (
            auditLogItems.map((log) => {
              const summary = summarizeEconomyMetadata(log.metadataJson);
              return (
                <div key={log.id} className="flex flex-col gap-1 p-3 text-xs text-slate-600">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold text-slate-900">{log.actorName ?? "Sistem"}</span>
                    <span className="text-[11px] text-slate-500">{formatAuditDate(log.occurredAtUtc)}</span>
                  </div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Rol</div>
                  <div>{log.actorRole ?? "-"}</div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Özet</div>
                  <div>{summary}</div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="bg-white rounded shadow p-4 text-sm">
        <div className="font-medium mb-2">Packages</div>
        <div className="mb-3 flex items-center gap-2 text-sm">
          <label className="text-xs font-semibold text-slate-600" htmlFor="package-filter">
            Filter
          </label>
          <input
            id="package-filter"
            type="text"
            value={packageFilter}
            onChange={(e) => setPackageFilter(e.target.value)}
            placeholder="Search by name"
            className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <span className="text-xs text-slate-500">
            {filteredPackages.length}/{packages.length}
          </span>
        </div>
        <div className="rounded border border-gray-200 p-3 mb-4">
          <div className="text-sm font-medium mb-2">Create new package</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
            <input
              placeholder="Package name"
              className="rounded border p-2"
              value={newPkg.name}
              onChange={(e) => setNewPkg({ ...newPkg, name: e.target.value })}
            />
            <input
              type="number"
              placeholder="Daily premium quota"
              className="rounded border p-2"
              value={newPkg.dailyQuotaPremium}
              onChange={(e) => setNewPkg({ ...newPkg, dailyQuotaPremium: Number(e.target.value) })}
            />
            <input
              type="number"
              placeholder="Daily free quota"
              className="rounded border p-2"
              value={newPkg.dailyQuotaFree}
              onChange={(e) => setNewPkg({ ...newPkg, dailyQuotaFree: Number(e.target.value) })}
            />
            <input
              type="number"
              placeholder="Duration (days)"
              className="rounded border p-2"
              value={newPkg.durationDays}
              onChange={(e) => setNewPkg({ ...newPkg, durationDays: Number(e.target.value) })}
            />
            <input
              type="number"
              placeholder="Price (Z)"
              className="rounded border p-2"
              value={newPkg.priceZ}
              onChange={(e) => setNewPkg({ ...newPkg, priceZ: Number(e.target.value) })}
            />
            <input
              placeholder="Description"
              className="rounded border p-2"
              value={newPkg.description}
              onChange={(e) => setNewPkg({ ...newPkg, description: e.target.value })}
            />
            <div className="flex flex-col items-center justify-center gap-2 rounded border border-dashed border-gray-300 p-3 text-center text-xs text-gray-500">
              {newPkg.imageUrl ? (
                <img
                  src={resolveAssetUrl(newPkg.imageUrl)}
                  alt="Package visual"
                  className="h-20 w-full rounded object-cover"
                />
              ) : (
                <span>Görsel seçilmedi</span>
              )}
              <label className="cursor-pointer rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-700">
                Görsel Yükle
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleNewPackageImageChange}
                />
              </label>
              {newPkg.imageUrl && (
                <button type="button" className="text-[11px] text-red-500 hover:underline" onClick={clearNewPackageImage}>
                  Görseli kaldır
                </button>
              )}
              {uploadingCreate && <span className="text-[11px] text-gray-400">Yükleniyor...</span>}
            </div>
            <div className="sm:col-span-6 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">İsim renkleri</div>
              {colors.length === 0 ? (
                <div className="text-[11px] text-gray-400">Renk seçenekleri tanımlandığında buradan seçebileceksin.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {colors.map((color) => {
                    const active = newPkg.colorOptionIds.includes(color.id);
                    return (
                      <button
                        key={color.id}
                        type="button"
                        className={`flex items-center gap-2 rounded-full border px-2 py-1 text-xs ${
                          active ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-300 text-gray-600 hover:border-emerald-400"
                        }`}
                        onClick={() => toggleNewPackageColor(color.id)}
                        disabled={!canEditPackages}
                      >
                        <span
                          className="h-4 w-4 rounded-full border border-white/40"
                          style={{ backgroundColor: color.hexCode }}
                        />
                        <span>{color.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="sm:col-span-6 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Avatar çerçeveleri</div>
              {frames.length === 0 ? (
                <div className="text-[11px] text-gray-400">Çerçeveler tanımlandığında buradan seçebileceksin.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {frames.map((frame) => {
                    const active = newPkg.frameOptionIds.includes(frame.id);
                    const imageUrl = resolveAssetUrl(frame.imageUrl);
                    return (
                      <button
                        key={frame.id}
                        type="button"
                        className={`flex items-center gap-2 rounded-full border px-2 py-1 text-xs ${
                          active ? "border-sky-500 bg-sky-50 text-sky-700" : "border-gray-300 text-gray-600 hover:border-sky-400"
                        }`}
                        onClick={() => toggleNewPackageFrame(frame.id)}
                        disabled={!canEditPackages}
                      >
                        <span className="h-6 w-6 overflow-hidden rounded-full border border-white/40 bg-white/40">
                          {imageUrl ? (
                            <img src={imageUrl} alt={frame.name} className="h-full w-full object-cover" />
                          ) : (
                            <span className="flex h-full w-full items-center justify-center text-[10px] text-gray-500">Yok</span>
                          )}
                        </span>
                        <span>{frame.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="sm:col-span-6 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Arkaplanlar</div>
              {backgrounds.length === 0 ? (
                <div className="text-[11px] text-gray-400">Arkaplan seçenekleri tanımlandığında buradan seçebileceksin.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {backgrounds.map((background) => {
                    const active = newPkg.backgroundOptionIds.includes(background.id);
                    const previewStyle = getBackgroundPreviewStyle(background);
                    return (
                      <button
                        key={background.id}
                        type="button"
                        className={`flex items-center gap-2 rounded-full border px-2 py-1 text-xs ${
                          active ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-300 text-gray-600 hover:border-purple-400"
                        }`}
                        onClick={() => toggleNewPackageBackground(background.id)}
                        disabled={!canEditPackages}
                      >
                        <span
                          className="h-4 w-10 rounded-full border border-white/30"
                          style={{
                            backgroundImage: previewStyle || BACKGROUND_PREVIEW_FALLBACK,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        />
                        <span>{background.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3">
            <button onClick={createPackageClick} className="rounded bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700">
              Create package
            </button>
          </div>
        </div>

        {packages.length === 0 && <div className="text-gray-400">No packages defined yet.</div>}
        {packages.length > 0 && (
          <div className="divide-y">
            {filteredPackages.map((pkg, idx) => (
              <div key={pkg.id} className="space-y-3 py-3">
                <div className="grid items-start gap-3 sm:grid-cols-[repeat(5,minmax(0,1fr))_minmax(0,150px)_auto]">
                  <label className="text-sm">
                    <div className="text-gray-600">Package name</div>
                    <input
                      className="mt-1 w-full rounded border p-2"
                      value={pkg.name}
                      onChange={(e) =>
                        setPackages((arr) =>
                          arr.map((p, i) => (i === idx ? { ...p, name: e.target.value } : p))
                        )
                      }
                    />
                  </label>
                  <label className="text-sm">
                    <div className="text-gray-600">Daily premium quota</div>
                    <input
                      type="number"
                      className="mt-1 w-full rounded border p-2"
                      value={pkg.dailyQuotaPremium}
                      onChange={(e) =>
                        setPackages((arr) =>
                          arr.map((p, i) => (i === idx ? { ...p, dailyQuotaPremium: Number(e.target.value) } : p))
                        )
                      }
                    />
                  </label>
                  <label className="text-sm">
                    <div className="text-gray-600">Daily free quota</div>
                    <input
                      type="number"
                      className="mt-1 w-full rounded border p-2"
                      value={pkg.dailyQuotaFree}
                      onChange={(e) =>
                        setPackages((arr) =>
                          arr.map((p, i) => (i === idx ? { ...p, dailyQuotaFree: Number(e.target.value) } : p))
                        )
                      }
                    />
                  </label>
                  <label className="text-sm">
                    <div className="text-gray-600">Duration (days)</div>
                    <input
                      type="number"
                      className="mt-1 w-full rounded border p-2"
                      value={pkg.durationDays}
                      onChange={(e) =>
                        setPackages((arr) =>
                          arr.map((p, i) => (i === idx ? { ...p, durationDays: Number(e.target.value) } : p))
                        )
                      }
                    />
                  </label>
                  <label className="text-sm">
                    <div className="text-gray-600">Price (Z)</div>
                    <input
                      type="number"
                      className="mt-1 w-full rounded border p-2"
                      value={pkg.priceZ}
                      onChange={(e) =>
                        setPackages((arr) =>
                          arr.map((p, i) => (i === idx ? { ...p, priceZ: Number(e.target.value) } : p))
                        )
                      }
                    />
                  </label>
                  <label className="text-sm">
                    <div className="text-gray-600">Description</div>
                    <input
                      className="mt-1 w-full rounded border p-2"
                      value={pkg.description ?? ""}
                      onChange={(e) =>
                        setPackages((arr) =>
                          arr.map((p, i) => (i === idx ? { ...p, description: e.target.value } : p))
                        )
                      }
                    />
                  </label>
                  <div className="flex flex-col items-center gap-2 rounded border border-dashed border-gray-300 bg-gray-50/40 p-3 text-center text-xs text-gray-500">
                    {pkg.imageUrl ? (
                      <img
                        src={resolveAssetUrl(pkg.imageUrl)}
                        alt={`${pkg.name} görseli`}
                        className="h-20 w-full rounded object-cover"
                      />
                    ) : (
                      <span>Görsel yok</span>
                    )}
                    <label className="cursor-pointer rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-700">
                      Görsel Yükle
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(event) => handlePackageImageChange(pkg, event)}
                        disabled={uploadingPackageId === pkg.id || !canEditPackages}
                      />
                    </label>
                    {pkg.imageUrl && (
                      <button
                        type="button"
                        className="text-[11px] text-red-500 hover:underline"
                        onClick={() => clearPackageImage(pkg)}
                        disabled={!canEditPackages}
                      >
                        Görseli kaldır
                      </button>
                    )}
                    {uploadingPackageId === pkg.id && <span className="text-[11px] text-gray-400">Yükleniyor...</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:flex-col sm:items-stretch">
                    <button
                      onClick={() => savePackage(pkg)}
                      className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                      disabled={!canEditPackages}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => deletePackage(pkg)}
                      className="rounded bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
                      disabled={!canEditPackages}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="grid gap-3 lg:grid-cols-3">
                  <div className="space-y-1">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">İsim renkleri</div>
                    {colors.length === 0 ? (
                      <div className="text-[11px] text-gray-400">Tanımlı renk seçeneği yok.</div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {colors.map((color) => {
                          const active = pkg.colorOptionIds.includes(color.id);
                          return (
                            <button
                              key={color.id}
                              type="button"
                              className={`flex items-center gap-2 rounded-full border px-2 py-1 text-xs ${
                                active
                                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                  : "border-gray-300 text-gray-600 hover:border-emerald-400"
                              }`}
                              onClick={() => togglePackageColor(pkg.id, color.id)}
                              disabled={!canEditPackages}
                            >
                              <span
                                className="h-4 w-4 rounded-full border border-white/40"
                                style={{ backgroundColor: color.hexCode }}
                              />
                              <span>{color.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Avatar çerçeveleri</div>
                    {frames.length === 0 ? (
                      <div className="text-[11px] text-gray-400">Tanımlı çerçeve yok.</div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {frames.map((frame) => {
                          const active = pkg.frameOptionIds.includes(frame.id);
                          const imageUrl = resolveAssetUrl(frame.imageUrl);
                          return (
                            <button
                              key={frame.id}
                              type="button"
                              className={`flex items-center gap-2 rounded-full border px-2 py-1 text-xs ${
                                active ? "border-sky-500 bg-sky-50 text-sky-700" : "border-gray-300 text-gray-600 hover:border-sky-400"
                              }`}
                              onClick={() => togglePackageFrame(pkg.id, frame.id)}
                              disabled={!canEditPackages}
                            >
                              <span className="h-6 w-6 overflow-hidden rounded-full border border-white/40 bg-white/40">
                                {imageUrl ? (
                                  <img src={imageUrl} alt={frame.name} className="h-full w-full object-cover" />
                                ) : (
                                  <span className="flex h-full w-full items-center justify-center text-[10px] text-gray-500">Yok</span>
                                )}
                              </span>
                              <span>{frame.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Arkaplanlar</div>
                    {backgrounds.length === 0 ? (
                      <div className="text-[11px] text-gray-400">Tanımlı arkaplan yok.</div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {backgrounds.map((background) => {
                          const active = pkg.backgroundOptionIds.includes(background.id);
                          const previewStyle = getBackgroundPreviewStyle(background);
                          return (
                            <button
                              key={background.id}
                              type="button"
                              className={`flex items-center gap-2 rounded-full border px-2 py-1 text-xs ${
                                active
                                  ? "border-purple-500 bg-purple-50 text-purple-700"
                                  : "border-gray-300 text-gray-600 hover:border-purple-400"
                              }`}
                              onClick={() => togglePackageBackground(pkg.id, background.id)}
                              disabled={!canEditPackages}
                            >
                              <span
                                className="h-4 w-8 rounded-full border border-white/30"
                                style={{
                                  backgroundImage: previewStyle || BACKGROUND_PREVIEW_FALLBACK,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                }}
                              />
                              <span>{background.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded shadow p-4 text-sm">
        <div className="flex items-center justify-between">
          <div className="font-medium">Profil görünüm kataloğu</div>
          {!canEditPackages && <span className="text-xs text-amber-600">Düzenleme yetkin yok.</span>}
        </div>
        <div className="mt-4 grid gap-6 lg:grid-cols-3">
          <div className="space-y-3 rounded border border-gray-200 p-3">
            <div className="text-sm font-semibold text-gray-700">İsim renkleri</div>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-xs text-gray-600">
                Ad
                <input
                  className="mt-1 w-full rounded border p-2 text-sm"
                  value={newColor.name}
                  onChange={(e) => setNewColor((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={!canEditPackages}
                />
              </label>
              <label className="text-xs text-gray-600">
                Hex
                <input
                  className="mt-1 w-full rounded border p-2 text-sm uppercase"
                  value={newColor.hexCode}
                  onChange={(e) => setNewColor((prev) => ({ ...prev, hexCode: e.target.value }))}
                  disabled={!canEditPackages}
                />
              </label>
              <label className="text-xs text-gray-600">
                Sıra
                <input
                  type="number"
                  className="mt-1 w-full rounded border p-2 text-sm"
                  value={newColor.sortOrder}
                  onChange={(e) => setNewColor((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
                  disabled={!canEditPackages}
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={newColor.isActive}
                  onChange={(e) => setNewColor((prev) => ({ ...prev, isActive: e.target.checked }))}
                  disabled={!canEditPackages}
                />
                Aktif
              </label>
            </div>
            <button
              type="button"
              onClick={handleCreateColor}
              className="w-full rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              disabled={!canEditPackages}
            >
              Yeni renk ekle
            </button>
            <div className="space-y-2">
              {colors.map((color, index) => (
                <div key={color.id} className="rounded border border-gray-200 p-2">
                  <div className="flex items-center gap-3">
                    <span
                      className="h-8 w-8 rounded-full border border-gray-300"
                      style={{ backgroundColor: color.hexCode }}
                    />
                    <input
                      className="flex-1 rounded border px-2 py-1 text-sm"
                      value={color.name}
                      onChange={(e) =>
                        setColors((arr) =>
                          arr.map((c, i) => (i === index ? { ...c, name: e.target.value } : c))
                        )
                      }
                      disabled={!canEditPackages}
                    />
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    <input
                      className="rounded border px-2 py-1 text-sm uppercase"
                      value={color.hexCode}
                      onChange={(e) =>
                        setColors((arr) =>
                          arr.map((c, i) => (i === index ? { ...c, hexCode: e.target.value } : c))
                        )
                      }
                      disabled={!canEditPackages}
                    />
                    <input
                      type="number"
                      className="rounded border px-2 py-1 text-sm"
                      value={color.sortOrder}
                      onChange={(e) =>
                        setColors((arr) =>
                          arr.map((c, i) => (i === index ? { ...c, sortOrder: Number(e.target.value) } : c))
                        )
                      }
                      disabled={!canEditPackages}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={color.isActive}
                        onChange={(e) =>
                          setColors((arr) =>
                            arr.map((c, i) => (i === index ? { ...c, isActive: e.target.checked } : c))
                          )
                        }
                        disabled={!canEditPackages}
                      />
                      Aktif
                    </label>
                  </div>
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => handleUpdateColor(color)}
                      className="rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50"
                      disabled={!canEditPackages}
                    >
                      Kaydet
                    </button>
                  </div>
                </div>
              ))}
              {colors.length === 0 && <div className="text-[11px] text-gray-400">Tanımlı renk bulunmuyor.</div>}
            </div>
          </div>

          <div className="space-y-3 rounded border border-gray-200 p-3">
            <div className="text-sm font-semibold text-gray-700">Avatar çerçeveleri</div>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-xs text-gray-600">
                Ad
                <input
                  className="mt-1 w-full rounded border p-2 text-sm"
                  value={newFrame.name}
                  onChange={(e) => setNewFrame((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={!canEditPackages}
                />
              </label>
              <label className="text-xs text-gray-600">
                Sıra
                <input
                  type="number"
                  className="mt-1 w-full rounded border p-2 text-sm"
                  value={newFrame.sortOrder}
                  onChange={(e) => setNewFrame((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
                  disabled={!canEditPackages}
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={newFrame.isActive}
                  onChange={(e) => setNewFrame((prev) => ({ ...prev, isActive: e.target.checked }))}
                  disabled={!canEditPackages}
                />
                Aktif
              </label>
            </div>
            <div className="flex flex-col items-center gap-2 rounded border border-dashed border-gray-300 bg-gray-50/60 p-3 text-center text-xs text-gray-500">
              {newFrame.imageUrl ? (
                <img
                  src={resolveAssetUrl(newFrame.imageUrl)}
                  alt="Frame preview"
                  className="h-20 w-20 rounded-full border border-white/40 object-cover"
                />
              ) : (
                <span>Görsel seçilmedi</span>
              )}
              <label className="cursor-pointer rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-700">
                Görsel Yükle
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleNewFrameImageUpload}
                  disabled={uploadingFrameCreate || !canEditPackages}
                />
              </label>
              {uploadingFrameCreate && <span className="text-[11px] text-gray-400">Yükleniyor...</span>}
            </div>
            <button
              type="button"
              onClick={handleCreateFrame}
              className="w-full rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              disabled={!canEditPackages}
            >
              Yeni çerçeve ekle
            </button>
            <div className="space-y-2">
              {frames.map((frame, index) => (
                <div key={frame.id} className="rounded border border-gray-200 p-2">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 overflow-hidden rounded-full border border-gray-200 bg-white/50">
                      {frame.imageUrl ? (
                        <img src={resolveAssetUrl(frame.imageUrl)} alt={frame.name} className="h-full w-full object-cover" />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">Yok</span>
                      )}
                    </div>
                    <input
                      className="flex-1 rounded border px-2 py-1 text-sm"
                      value={frame.name}
                      onChange={(e) =>
                        setFrames((arr) =>
                          arr.map((f, i) => (i === index ? { ...f, name: e.target.value } : f))
                        )
                      }
                      disabled={!canEditPackages}
                    />
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    <input
                      type="number"
                      className="rounded border px-2 py-1 text-sm"
                      value={frame.sortOrder}
                      onChange={(e) =>
                        setFrames((arr) =>
                          arr.map((f, i) => (i === index ? { ...f, sortOrder: Number(e.target.value) } : f))
                        )
                      }
                      disabled={!canEditPackages}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={frame.isActive}
                        onChange={(e) =>
                          setFrames((arr) =>
                            arr.map((f, i) => (i === index ? { ...f, isActive: e.target.checked } : f))
                          )
                        }
                        disabled={!canEditPackages}
                      />
                      Aktif
                    </label>
                    <label className="cursor-pointer rounded bg-slate-800 px-3 py-1 text-center text-xs font-semibold text-white hover:bg-slate-700">
                      Görsel Değiştir
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(event) => handleExistingFrameImageChange(frame, event)}
                        disabled={uploadingFrameOptionId === frame.id || !canEditPackages}
                      />
                    </label>
                  </div>
                  {uploadingFrameOptionId === frame.id && <div className="text-[11px] text-gray-400">Görsel yükleniyor...</div>}
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => handleUpdateFrame(frame)}
                      className="rounded bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                      disabled={!canEditPackages}
                    >
                      Kaydet
                    </button>
                  </div>
                </div>
              ))}
              {frames.length === 0 && <div className="text-[11px] text-gray-400">Tanımlı çerçeve bulunmuyor.</div>}
            </div>
          </div>

          <div className="space-y-3 rounded border border-gray-200 p-3">
            <div className="text-sm font-semibold text-gray-700">Arkaplanlar</div>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-xs text-gray-600">
                Ad
                <input
                  className="mt-1 w-full rounded border p-2 text-sm"
                  value={newBackground.name}
                  onChange={(e) => setNewBackground((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={!canEditPackages}
                />
              </label>
              <label className="text-xs text-gray-600">
                CSS Değeri
                <textarea
                  className="mt-1 w-full rounded border p-2 text-sm"
                  value={newBackground.cssValue}
                  onChange={(e) => setNewBackground((prev) => ({ ...prev, cssValue: e.target.value }))}
                  placeholder="linear-gradient(...)"
                  disabled={!canEditPackages}
                />
              </label>
              <label className="text-xs text-gray-600">
                Görsel yolu
                <input
                  className="mt-1 w-full rounded border p-2 text-sm"
                  value={newBackground.imageUrl}
                  onChange={(e) => setNewBackground((prev) => ({ ...prev, imageUrl: e.target.value }))}
                  disabled={!canEditPackages}
                />
              </label>
              <label className="text-xs text-gray-600">
                Önizleme yolu
                <input
                  className="mt-1 w-full rounded border p-2 text-sm"
                  value={newBackground.previewUrl}
                  onChange={(e) => setNewBackground((prev) => ({ ...prev, previewUrl: e.target.value }))}
                  disabled={!canEditPackages}
                />
              </label>
              <label className="text-xs text-gray-600">
                Sıra
                <input
                  type="number"
                  className="mt-1 w-full rounded border p-2 text-sm"
                  value={newBackground.sortOrder}
                  onChange={(e) => setNewBackground((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
                  disabled={!canEditPackages}
                />
              </label>
              <div className="flex flex-col gap-2 text-xs text-gray-600">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newBackground.isActive}
                    onChange={(e) => setNewBackground((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={!canEditPackages}
                  />
                  Aktif
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newBackground.isDefault}
                    onChange={(e) => setNewBackground((prev) => ({ ...prev, isDefault: e.target.checked }))}
                    disabled={!canEditPackages}
                  />
                  Varsayılan
                </label>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
              <label className="cursor-pointer rounded bg-slate-800 px-3 py-1 font-semibold text-white hover:bg-slate-700">
                Görsel Yükle
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleBackgroundImageUpload}
                  disabled={uploadingBackgroundCreate || !canEditPackages}
                />
              </label>
              {newBackground.imageUrl && <span>Seçilen: {newBackground.imageUrl}</span>}
              {uploadingBackgroundCreate && <span>Yükleniyor...</span>}
            </div>
            <button
              type="button"
              onClick={handleCreateBackground}
              className="w-full rounded bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
              disabled={!canEditPackages}
            >
              Yeni arkaplan ekle
            </button>
            <div className="space-y-2">
              {backgrounds.map((background, index) => (
                <div key={background.id} className="rounded border border-gray-200 p-2 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span
                        className="h-8 w-16 rounded-full border border-white/30"
                        style={{
                          backgroundImage: getBackgroundPreviewStyle(background) || BACKGROUND_PREVIEW_FALLBACK,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      />
                      <input
                        className="rounded border px-2 py-1 text-sm"
                        value={background.name}
                        onChange={(e) =>
                          setBackgrounds((arr) =>
                            arr.map((bg, i) => (i === index ? { ...bg, name: e.target.value } : bg)),
                          )
                        }
                        disabled={!canEditPackages}
                      />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={background.isActive}
                        onChange={(e) =>
                          setBackgrounds((arr) =>
                            arr.map((bg, i) => (i === index ? { ...bg, isActive: e.target.checked } : bg)),
                          )
                        }
                        disabled={!canEditPackages}
                      />
                      Aktif
                    </label>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <textarea
                      className="rounded border px-2 py-1 text-sm"
                      value={background.cssValue ?? ""}
                      placeholder="linear-gradient(...)"
                      onChange={(e) =>
                        setBackgrounds((arr) =>
                          arr.map((bg, i) => (i === index ? { ...bg, cssValue: e.target.value } : bg)),
                        )
                      }
                      disabled={!canEditPackages}
                    />
                    <div className="space-y-2 text-xs text-gray-600">
                      <input
                        className="w-full rounded border px-2 py-1 text-sm"
                        placeholder="Görsel yolu"
                        value={background.imageUrl ?? ""}
                        onChange={(e) =>
                          setBackgrounds((arr) =>
                            arr.map((bg, i) => (i === index ? { ...bg, imageUrl: e.target.value } : bg)),
                          )
                        }
                        disabled={!canEditPackages}
                      />
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer rounded bg-slate-800 px-2 py-1 text-white hover:bg-slate-700">
                          Görsel Yükle
                          <input
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={(event) => handleExistingBackgroundImageChange(background, event)}
                            disabled={uploadingBackgroundOptionId === background.id || !canEditPackages}
                          />
                        </label>
                        {uploadingBackgroundOptionId === background.id && <span>Yükleniyor...</span>}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <input
                      type="number"
                      className="rounded border px-2 py-1 text-sm"
                      value={background.sortOrder}
                      onChange={(e) =>
                        setBackgrounds((arr) =>
                          arr.map((bg, i) => (i === index ? { ...bg, sortOrder: Number(e.target.value) } : bg)),
                        )
                      }
                      disabled={!canEditPackages}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={Boolean(background.isDefault)}
                        onChange={(e) =>
                          setBackgrounds((arr) =>
                            arr.map((bg, i) => (i === index ? { ...bg, isDefault: e.target.checked } : bg)),
                          )
                        }
                        disabled={!canEditPackages}
                      />
                      Varsayılan
                    </label>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => handleUpdateBackground(background)}
                      className="rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50"
                      disabled={!canEditPackages}
                    >
                      Kaydet
                    </button>
                  </div>
                </div>
              ))}
              {backgrounds.length === 0 && <div className="text-[11px] text-gray-400">Tanımlı arkaplan bulunmuyor.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded bg-white p-4 text-sm shadow">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function formatAuditDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("tr-TR");
}

function summarizeEconomyMetadata(json?: string) {
  if (!json) {
    return "Detay yok.";
  }
  try {
    const data = JSON.parse(json) as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof data.freeDailyQuota === "number") {
      parts.push(`Ücretsiz kota: ${data.freeDailyQuota}`);
    }
    if (typeof data.singleUnlockPriceZ === "number") {
      parts.push(`Tek bölüm (Z): ${data.singleUnlockPriceZ}`);
    }
    if (typeof data.creatorDailyPartLimit === "number") {
      parts.push(`Günlük bölüm limiti: ${data.creatorDailyPartLimit}`);
    }
    if (typeof data.creatorRevenueSharePercent === "number") {
      parts.push(`Creator payı: ${data.creatorRevenueSharePercent}%`);
    }
    if (typeof data.tokenToTryRate === "number") {
      const currency = typeof data.creatorPayoutCurrencyCode === "string" && data.creatorPayoutCurrencyCode.length > 0
        ? data.creatorPayoutCurrencyCode
        : "TRY";
      parts.push(`1 ZTN ≈ ${data.tokenToTryRate} ${currency}`);
    } else if (typeof data.creatorPayoutCurrencyCode === "string" && data.creatorPayoutCurrencyCode.length > 0) {
      parts.push(`Para birimi: ${data.creatorPayoutCurrencyCode}`);}
    return parts.length > 0 ? parts.join(" • ") : "Detay yok.";
  } catch {
    return json;
  }
}
